/**
 * Accounting System Constants
 */

// ============================================================================
// ACCOUNTING SYSTEMS
// ============================================================================

export enum AccountingSystem {
  FORTNOX = "fortnox",
  ECONOMIC = "economic",
  POWEROFFICE = "poweroffice",
}

// ============================================================================
// INVOICE STATUSES
// ============================================================================

export enum InvoiceStatus {
  DRAFT = "draft",
  SENT = "sent",
  PAID = "paid",
  CANCELLED = "cancelled",
  OVERDUE = "overdue",
}

// ============================================================================
// FIELD LIMITS
// ============================================================================

// Longest value Fortnox accepts in its "Your Reference" / "Our Reference"
// fields (invoice and customer). The invoice reference a customer or staff
// member types is sent as "Your Reference", so every input for it is capped
// here; anything longer made Fortnox reject the invoice ("Er referens
// överskrider maximal längd").
export const INVOICE_REFERENCE_MAX_LENGTH = 50;

// ============================================================================
// ARTICLE CODES
// Hardcoded article codes for special items that don't have UI mapping
// ============================================================================

export const SPECIAL_ARTICLE_CODES = {
  // Road show article code - used for road show bookings which don't have
  // a traditional show mapping in the system
  ROAD_SHOW: "132120", // Update this to match your Fortnox article code
} as const;

// ============================================================================
// VAT RATES
// Standard VAT rates used in Nordic countries:
// - 0%: Exempt items
// - 6%: Newspapers, books, passenger transport (Sweden)
// - 12%: Food, hotels, cultural services (Sweden/Norway)
// - 25%: Standard rate (Sweden/Norway/Denmark)
// ============================================================================

export enum VatRate {
  RATE_0 = 0,
  RATE_6 = 6,
  RATE_12 = 12,
  RATE_25 = 25,
}

// ============================================================================
// HELPERS
// ============================================================================

// Helper to get VAT multiplier (e.g., 25 -> 1.25)
export function getVatMultiplier(rate: VatRate): number {
  return 1 + rate / 100;
}

// Helper to calculate VAT amount from net price
export function calculateVatAmount(netPrice: number, rate: VatRate): number {
  return netPrice * (rate / 100);
}

// Helper to calculate gross price from net price
export function calculateGrossPrice(netPrice: number, rate: VatRate): number {
  return netPrice * getVatMultiplier(rate);
}

// Helper to calculate net price from gross price
export function calculateNetPrice(grossPrice: number, rate: VatRate): number {
  return grossPrice / getVatMultiplier(rate);
}

// Invoice payment term in days (due date = invoice date + this). A venue
// gets a listed term when one of its names (venue_infos.name, any language)
// contains the key, ignoring case, e.g. "OnAir Copenhagen". Any other venue
// uses the default. A partner's own payment term still takes priority.
export const DEFAULT_PAYMENT_TERM_DAYS = 20;

const VENUE_PAYMENT_TERM_DAYS: Record<string, number> = {
  copenhagen: 7,
  københavn: 7,
};

export function getVenuePaymentTermDays(
  venueNames: Array<string | null | undefined>,
): number {
  for (const name of venueNames) {
    const lower = name?.toLowerCase() ?? "";
    for (const [key, days] of Object.entries(VENUE_PAYMENT_TERM_DAYS)) {
      if (lower.includes(key)) return days;
    }
  }
  return DEFAULT_PAYMENT_TERM_DAYS;
}
